import {useMutation, useQueryClient} from '@tanstack/react-query';
import {CheckCircle2, CircleHelp, FileText} from 'lucide-react';
import {Link} from '../router';
import {ApiError, api} from '../api';
import {SecondaryButton, Surface} from '../design';

type MissingQuestion = {id: string; prompt: string};

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;

export const coverLetterQuestions = (error: unknown): MissingQuestion[] => {
  if (!(error instanceof ApiError) || error.code !== 'cover_letter_information_required') return [];
  const questions = record(error.details)?.questions;
  if (!Array.isArray(questions)) return [];
  return questions.slice(0, 10).flatMap((value, index) => {
    const item = record(value);
    const prompt = typeof item?.prompt === 'string' ? item.prompt.trim().slice(0, 500) : '';
    if (!prompt) return [];
    const id = typeof item?.id === 'string' && item.id.trim() ? item.id.trim().slice(0, 128) : `question-${index + 1}`;
    return [{id, prompt}];
  });
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'The cover letter could not be prepared.';

export const CoverLetterAction = ({opportunityId}: {opportunityId: string}) => {
  const queryClient = useQueryClient();
  const request = useMutation({
    mutationFn: () => api.requestCoverLetter(opportunityId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['documents']}),
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['opportunity', opportunityId]}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
    },
  });
  const questions = coverLetterQuestions(request.error);
  const missingInformation = request.error instanceof ApiError && request.error.code === 'cover_letter_information_required';

  return (
    <Surface className="pd-trust-banner pd-cover-letter-action">
      <FileText size={23} aria-hidden="true" />
      <div className="pd-cover-letter-action__copy">
        <strong>Create a tailored cover letter</strong>
        <p>Build a draft from approved career evidence for this role. You review the PDF before using it; PursuitDeck never attaches or submits it automatically.</p>
        {request.isSuccess ? (
          <div className="pd-inline-status" role="status" aria-live="polite">
            <CheckCircle2 size={17} aria-hidden="true" />
            <span>
              {request.data.state === 'approved'
                ? 'Your approved cover letter is ready. '
                : request.data.reused
                  ? 'Your existing cover-letter draft is ready. '
                  : 'Your cover-letter draft is ready. '}
              <Link to="/documents">Review the exact PDF in Documents.</Link>
            </span>
          </div>
        ) : null}
        {missingInformation ? (
          <div className="pd-cover-letter-questions" role="alert">
            <strong><CircleHelp size={16} aria-hidden="true" />A few facts are needed first</strong>
            {questions.length ? (
              <ul>{questions.map((question) => <li key={question.id}>{question.prompt}</li>)}</ul>
            ) : (
              <p>{errorMessage(request.error)}</p>
            )}
            <p>No letter was generated. The questions are also waiting in your Action Inbox.</p>
            <Link to="/career-profile">Review career facts and approved bullets</Link>
          </div>
        ) : null}
        {request.isError && !missingInformation ? (
          <p className="pd-form-error" role="alert">{errorMessage(request.error)}</p>
        ) : null}
      </div>
      <SecondaryButton
        loading={request.isPending}
        disabled={!opportunityId}
        onClick={() => request.mutate()}
      >
        Create cover letter draft
      </SecondaryButton>
    </Surface>
  );
};
